import accountGuideMarker from '@/assets/icons/account-guide-marker.svg';
import CreatorQuoteDesignIcon from './CreatorQuoteDesignIcon';
import { Button } from 'aha-components';
import { Popover, Tooltip } from 'antd';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ACCOUNT_GUIDE_DISMISSED_KEY = 'creator-account-guide-dismissed';

export default function CreatorAddAccountsButton({
  mobile,
  menuOpen,
  disabled = false,
  disabledReason,
  onClick
}: {
  mobile: boolean;
  menuOpen: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const guideId = useId();
  const [guideOpen, setGuideOpen] = useState(() => {
    try {
      return localStorage.getItem(ACCOUNT_GUIDE_DISMISSED_KEY) !== 'true';
    } catch {
      return true;
    }
  });
  // A disabled entry must never leave its blocking onboarding overlay active.
  const showGuide = guideOpen && !disabled && !menuOpen;
  useEffect(() => {
    if (!showGuide) return;
    const isGuideAction = (target: EventTarget | null) =>
      target instanceof Element &&
      target
        .closest('[data-account-guide-action]')
        ?.getAttribute('data-account-guide-action') === guideId;
    const blockOutsideAction = (event: Event) => {
      if (isGuideAction(event.target)) return;
      // Preserve the containing drawer's native backdrop-close behavior.
      if (
        event.target instanceof Element &&
        event.target.matches(
          '.aha-drawer-root > [aria-hidden], .MuiBackdrop-root'
        )
      )
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    const blockDismissKey = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' ||
        ((event.key === 'Enter' || event.key === ' ') &&
          !isGuideAction(event.target))
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    document.addEventListener('pointerdown', blockOutsideAction, true);
    document.addEventListener('click', blockOutsideAction, true);
    document.addEventListener('keydown', blockDismissKey, true);
    return () => {
      document.removeEventListener('pointerdown', blockOutsideAction, true);
      document.removeEventListener('click', blockOutsideAction, true);
      document.removeEventListener('keydown', blockDismissKey, true);
    };
  }, [showGuide, guideId]);

  const dismissGuide = () => {
    setGuideOpen(false);
    try {
      localStorage.setItem(ACCOUNT_GUIDE_DISMISSED_KEY, 'true');
    } catch {
      // Storage may be unavailable; still dismiss the current guide.
    }
  };

  return (
    <>
      {showGuide ? (
        <div
          data-testid="account-guide-mask"
          aria-hidden="true"
          className="fixed inset-0 z-50 bg-white/80"
        />
      ) : null}
      <Tooltip
        title={disabled ? disabledReason : undefined}
        trigger={mobile ? 'click' : 'hover'}
        zIndex={mobile ? 10010 : undefined}
      >
        <span
          className="inline-flex max-md:w-full"
          tabIndex={disabled ? 0 : undefined}
        >
          <Button
            type="primary"
            htmlType="button"
            size="sm"
            className={`!h-8 min-w-[142px] !gap-1 !bg-[#FF621D] !px-2.5 !py-1.5 max-md:w-full disabled:pointer-events-none disabled:opacity-50 disabled:!text-white ${showGuide ? 'z-[51]' : ''}`}
            disabled={disabled}
            data-account-guide-action={guideId}
            icon={
              <CreatorQuoteDesignIcon
                name="userPlus"
                className="h-4 w-4 text-white"
              />
            }
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => {
              if (disabled) return;
              dismissGuide();
              onClick();
            }}
          >
            {t('multi_platform.quote.add_accounts', {
              defaultValue: 'Add accounts'
            })}
          </Button>
        </span>
      </Tooltip>
      {showGuide ? (
        <Popover
          open={showGuide}
          // The mobile quote drawer uses z-index 10000; this portal mounts on body.
          zIndex={mobile ? 10010 : undefined}
          placement={mobile ? 'bottom' : 'bottomRight'}
          arrow={{ pointAtCenter: true }}
          trigger={[]}
          overlayStyle={{ maxWidth: 'calc(100vw - 32px)' }}
          content={
            <div className="w-[288px] max-w-[calc(100vw-56px)] text-sm leading-5 text-[#404040]">
              <p className="m-0 font-semibold text-[#171717]">
                {t('multi_platform.quote.account_guide_title', {
                  defaultValue: 'More accounts, more earning potential'
                })}
              </p>
              <p className="mb-3 mt-1">
                {t('multi_platform.quote.account_guide_description', {
                  defaultValue:
                    'Click “Add accounts” above to add more accounts and offer a collaboration package with a single quote.'
                })}
              </p>
              <Button
                type="link-color"
                size="sm"
                className="!h-auto !p-0"
                data-account-guide-action={guideId}
                onClick={dismissGuide}
              >
                {t('multi_platform.quote.account_guide_dismiss', {
                  defaultValue: 'Got it'
                })}
              </Button>
            </div>
          }
        >
          <span
            aria-hidden="true"
            className="absolute -bottom-2 left-1/2 z-[51] flex h-4 w-4 -translate-x-1/2 items-center justify-center rounded-full border-0 bg-transparent p-0"
          >
            <img src={accountGuideMarker} alt="" width={16} height={16} />
          </span>
        </Popover>
      ) : null}
    </>
  );
}
